import { normalizeAssignmentPermissions } from "../../../../components/request/requestAssignmentDisplay";
import { LOAN_PROCESS_STATUSES } from "../../../../components/request/case-tabs/lendingOperationsConstants";
import { matchesPartnerReferralFilter } from "../shared/partnerReferralFilterUtils";

/** فیلدهای قابل جستجو — اگر در row نباشند نادیده گرفته می‌شوند. */
export const ALL_REQUESTS_SEARCH_FIELDS = [
  "firstName",
  "lastName",
  "fullName",
  "mobile",
  "nationalCode",
  "NationalCode",
  "companyNationalCode",
  "electricBoillNumber",
  "trackingCode",
  "TrackingCode",
  "referenceID",
  "provinceName",
  "cityName",
  "address",
  "assignedOperatorFullName",
  "assignedOperatorDisplay",
  "requestConfirmTitle",
  "stepTitle",
  "stepInfoTitle",
  "powerPlantTypeTitle",
  "locationUsageTypeTitle",
  "applicantGoalsTitle",
  "leadPriorityTitle",
  "followUpReminderTitle",
  "contractorName",
  "supervisorName",
  "partnerReferralDisplayName",
  "PartnerReferralDisplayName",
  "opsLendingLabel",
];

export const ALL_REQUESTS_STATUS_FILTERS = [
  { value: "", label: "همه" },
  {
    value: "NotChecked",
    label: "بررسی اولیه",
    titles: ["بررسی نشده", "بررسی اولیه"],
  },
  { value: "InProcess", label: "جاری", titles: ["جاری", "در حال پردازش"] },
  { value: "Completed", label: "تکمیل‌شده", titles: ["تکمیل شده", "تکمیل‌شده"] },
  { value: "Canceled", label: "لغوشده", titles: ["لغو شده", "لغوشده"] },
  { value: "Deleted", label: "حذف‌شده", titles: ["حذف شده", "حذف‌شده"] },
  {
    value: "ReturnToOperator",
    label: "ارجاع به کارشناس",
    titles: ["ارجاع به کارشناس"],
  },
];

/** فیلتر وضعیت برای لیست «در جریان» — بدون لغو، حذف و ارجاع */
export const IN_PROCESS_STATUS_FILTERS = ALL_REQUESTS_STATUS_FILTERS.filter(
  (opt) => !["Canceled", "Deleted", "ReturnToOperator"].includes(opt.value)
);

/** فیلتر وضعیت وام در کارتابل — مقدار عددی یا none */
export const ALL_REQUESTS_LOAN_STATUS_FILTERS = [
  { value: "", label: "همه" },
  { value: "none", label: "بدون وام" },
  ...LOAN_PROCESS_STATUSES.map((s) => ({
    value: String(s.value),
    label: s.label,
  })),
];

const matchesStatusFilter = (row, statusFilter) => {
  if (!statusFilter) return true;
  const confirm = row.requestConfirm ?? row.RequestConfirm;
  if (confirm != null && String(confirm) === statusFilter) return true;
  const option = ALL_REQUESTS_STATUS_FILTERS.find((o) => o.value === statusFilter);
  const title = (row.requestConfirmTitle || "").trim();
  if (!option?.titles?.length) return false;
  return option.titles.some(
    (t) => title === t || title.includes(t) || t.includes(title)
  );
};

const matchesProvinceFilter = (row, provinceFilter) => {
  if (provinceFilter === "" || provinceFilter == null) return true;
  const rowProvinceId = Number(row?.provinceId ?? row?.ProvinceId ?? 0);
  return rowProvinceId > 0 && rowProvinceId === Number(provinceFilter);
};

const matchesCityFilter = (row, cityFilter) => {
  if (cityFilter === "" || cityFilter == null) return true;
  const rowCityId = Number(row?.cityId ?? row?.CityId ?? 0);
  return rowCityId > 0 && rowCityId === Number(cityFilter);
};

const matchesLoanStatusFilter = (row, loanStatusFilter) => {
  if (loanStatusFilter === "" || loanStatusFilter == null) return true;

  const current =
    row?.lendingCurrentStatus ??
    row?.LendingCurrentStatus ??
    null;
  const hasStatus =
    current != null && current !== "" && !Number.isNaN(Number(current));

  if (loanStatusFilter === "none") {
    if (hasStatus) return false;
    const label = (row?.opsLendingLabel || "").trim();
    return (
      !label ||
      label === OPS_NOT_AVAILABLE_LABEL ||
      label === OPS_EMPTY_LABEL ||
      label === "—"
    );
  }

  const wanted = Number(loanStatusFilter);
  if (Number.isNaN(wanted)) return true;

  if (hasStatus) return Number(current) === wanted;

  const option = LOAN_PROCESS_STATUSES.find((s) => s.value === wanted);
  if (!option) return false;
  const label = (row?.opsLendingLabel || row?.lendingSummaryTitle || "").trim();
  return label === option.label || label.startsWith(`${option.label} `) || label.startsWith(`${option.label}·`) || label.startsWith(`${option.label} ·`);
};

const buildHaystack = (row) => {
  const parts = [];
  ALL_REQUESTS_SEARCH_FIELDS.forEach((key) => {
    const v = row?.[key];
    if (v != null && v !== "") parts.push(String(v));
  });
  parts.push(
    `${row?.firstName || ""} ${row?.lastName || ""}`.trim(),
    row?.applicantFullName || ""
  );
  return parts.join(" ").toLowerCase();
};

export const filterAllRequestsRows = (
  rows,
  {
    searchText = "",
    statusFilter = "",
    partnerReferralFilter = "",
    provinceFilter = "",
    cityFilter = "",
    loanStatusFilter = "",
  } = {}
) => {
  const q = (searchText || "").trim().toLowerCase();
  return rows.filter((row) => {
    if (!matchesStatusFilter(row, statusFilter)) return false;
    if (!matchesPartnerReferralFilter(row, partnerReferralFilter)) return false;
    if (!matchesProvinceFilter(row, provinceFilter)) return false;
    if (!matchesCityFilter(row, cityFilter)) return false;
    if (!matchesLoanStatusFilter(row, loanStatusFilter)) return false;
    if (!q) return true;
    return buildHaystack(row).includes(q);
  });
};

export const OPS_NOT_AVAILABLE_LABEL = "ندارد";
export const OPS_EMPTY_LABEL = "ثبت نشده";

const looksLikeIranianMobile = (value) => {
  if (value == null || value === "") return false;
  const digits = String(value).replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("09");
};

/** نام متقاضی برای کارتابل — با fallback از fullName و UserName */
export const resolveApplicantFullName = (row) => {
  const first = row?.firstName ?? row?.FirstName ?? "";
  const last = row?.lastName ?? row?.LastName ?? "";
  const combined = `${first} ${last}`.trim();
  return combined || row?.fullName || row?.FullName || "—";
};

/** موبایل متقاضی — با fallback از userName (ورود OTP) */
export const resolveApplicantMobile = (row) => {
  const mobile = row?.mobile ?? row?.Mobile;
  if (mobile != null && String(mobile).trim() !== "") return String(mobile).trim();
  const userName = row?.userName ?? row?.UserName;
  if (looksLikeIranianMobile(userName)) return String(userName).trim();
  return "—";
};

/** یکسان‌سازی فیلدهای هویتی برای لیست و فرم کارشناس */
export const enrichApplicantIdentityFields = (row) => {
  if (!row) return row;

  const firstName = (row.firstName ?? row.FirstName ?? "").trim();
  const lastName = (row.lastName ?? row.LastName ?? "").trim();
  const applicantFullName = resolveApplicantFullName(row);
  const mobileResolved = resolveApplicantMobile(row);
  const existingFull = (row.fullName ?? row.FullName ?? "").trim();

  return {
    ...row,
    firstName,
    lastName,
    fullName:
      existingFull ||
      (applicantFullName !== "—" ? applicantFullName : ""),
    applicantFullName,
    mobile:
      mobileResolved !== "—"
        ? mobileResolved
        : (row.mobile ?? row.Mobile ?? "").trim() || "",
  };
};

export const pickChipLabel = (value, fallback = OPS_EMPTY_LABEL) => {
  const v =
    value != null && String(value).trim() !== "" ? String(value).trim() : "";
  if (!v || v === "—" || v === "-") return fallback;
  return v;
};

export const buildLocationLookupMaps = (provinces = [], cities = []) => {
  const provinceById = new Map();
  for (const province of provinces) {
    const id = Number(province?.id ?? province?.value);
    const name = (
      province?.provinceName ??
      province?.ProvinceName ??
      province?.label ??
      province?.title ??
      ""
    ).trim();
    if (id > 0 && name) provinceById.set(id, name);
  }

  const cityById = new Map();
  for (const city of cities) {
    const id = Number(city?.id ?? city?.value);
    if (id <= 0) continue;
    cityById.set(id, {
      cityName: (city?.cityName ?? city?.CityName ?? city?.label ?? "").trim(),
      provinceId: Number(city?.provinceId ?? city?.ProvinceId ?? 0) || null,
      provinceName: (city?.provinceName ?? city?.ProvinceName ?? "").trim(),
    });
  }

  return { provinceById, cityById };
};

export const resolveRowProvinceName = (row, locationMaps) => {
  const direct = (row?.provinceName ?? row?.ProvinceName ?? "").trim();
  if (direct && direct !== "—") return direct;

  if (!locationMaps) return direct || "—";

  const provinceId = Number(row?.provinceId ?? row?.ProvinceId ?? 0);
  if (provinceId > 0 && locationMaps.provinceById.has(provinceId)) {
    return locationMaps.provinceById.get(provinceId);
  }

  const cityId = Number(row?.cityId ?? row?.CityId ?? 0);
  if (cityId > 0 && locationMaps.cityById.has(cityId)) {
    const city = locationMaps.cityById.get(cityId);
    if (city.provinceName) return city.provinceName;
    if (
      city.provinceId &&
      locationMaps.provinceById.has(city.provinceId)
    ) {
      return locationMaps.provinceById.get(city.provinceId);
    }
  }

  return "—";
};

export const resolveRowCityName = (row, locationMaps) => {
  const direct = (row?.cityName ?? row?.CityName ?? "").trim();
  if (direct && direct !== "—") return direct;

  if (!locationMaps) return direct || "—";

  const cityId = Number(row?.cityId ?? row?.CityId ?? 0);
  if (cityId > 0 && locationMaps.cityById.has(cityId)) {
    const city = locationMaps.cityById.get(cityId);
    if (city.cityName) return city.cityName;
  }

  return "—";
};

export const mapOperationsSummaryChips = (row) => ({
  opsMehrsanLabel: pickChipLabel(
    row.mehrsanSummaryTitle ?? row.MehrsanSummaryTitle,
    OPS_NOT_AVAILABLE_LABEL
  ),
  opsMehrsanColor:
    row.mehrsanSummaryColor ?? row.MehrsanSummaryColor ?? "default",
  opsLendingLabel: pickChipLabel(
    row.lendingSummaryTitle ?? row.LendingSummaryTitle,
    OPS_NOT_AVAILABLE_LABEL
  ),
  opsLendingColor:
    row.lendingSummaryColor ?? row.LendingSummaryColor ?? "default",
  lendingCurrentStatus:
    row.lendingCurrentStatus ?? row.LendingCurrentStatus ?? null,
});

export const enrichAllRequestRows = (list, currentUserId, locationMaps = null) => {
  return (Array.isArray(list) ? list : []).map((row) => {
    const normalized = normalizeAssignmentPermissions(row);
    const assignedId = normalized.assignedOperatorUserId ?? null;
    const assignedName = normalized.assignedOperatorFullName ?? "";

    let assignedOperatorDisplay = "بدون مسئول";
    let assignedOperatorChipColor = "default";
    if (assignedId != null && assignedId !== "") {
      if (
        currentUserId != null &&
        Number(assignedId) === Number(currentUserId)
      ) {
        assignedOperatorDisplay = "شما";
        assignedOperatorChipColor = "success";
      } else {
        assignedOperatorDisplay = assignedName || "—";
      }
    }

    const identity = enrichApplicantIdentityFields(row);

    const rawPartnerReferralDisplayName =
      row.partnerReferralDisplayName ??
      row.PartnerReferralDisplayName ??
      "";
    const resolvedPartnerReferralDisplayName = String(rawPartnerReferralDisplayName).trim();

    const resolvedContractorName =
      row.contractorName ?? row.ContractorName ?? "";

    // When the request was introduced/registered via contractor marketing referral link,
    // backend can fill this field. In that case "معرف" column should show contractor name.
    const resolvedReferralSuggestedContractorCompanyName =
      row.referralSuggestedContractorCompanyName ??
      row.ReferralSuggestedContractorCompanyName ??
      "";

    const isEmptyDisplay = (v) =>
      !v || v === "—" || v === "-" || v === "—" || v === "";

    return applyWorkflowV2ListFields({
      ...normalized,
      id: row.id ?? row.Id,
      provinceId: row.provinceId ?? row.ProvinceId ?? null,
      cityId: row.cityId ?? row.CityId ?? null,
      provinceName: resolveRowProvinceName(row, locationMaps),
      cityName: resolveRowCityName(row, locationMaps),
      applicantFullName: identity.applicantFullName,
      mobile: identity.mobile,
      firstName: identity.firstName,
      lastName: identity.lastName,
      fullName: identity.fullName,
      assignedOperatorDisplay,
      assignedOperatorChipColor,
      partnerReferralDisplayName: !isEmptyDisplay(
        resolvedReferralSuggestedContractorCompanyName
      )
        ? `پیمانکار: ${resolvedReferralSuggestedContractorCompanyName}`
        : !isEmptyDisplay(resolvedPartnerReferralDisplayName)
          ? `بازاریاب: ${resolvedPartnerReferralDisplayName}`
          : resolvedContractorName
            ? `پیمانکار: ${resolvedContractorName}`
            : "",
      partnerReferralLevel1PartnerId:
        row.partnerReferralLevel1PartnerId ??
        row.PartnerReferralLevel1PartnerId ??
        null,
      ...mapOperationsSummaryChips(row),
    });
  });
};

/** WorkflowV2 columns returned inline from list GetList (avoids 2nd API round-trip). */
export const applyWorkflowV2ListFields = (row) => {
  const hasWorkflowV2 = Boolean(row.hasWorkflowV2 ?? row.HasWorkflowV2);
  const workflowV2StageTitle =
    row.workflowV2StageTitle ??
    row.WorkflowV2StageTitle ??
    "";
  const legacyStepTitle = row.stepTitle ?? row.StepTitle ?? "";
  const stepTitle =
    (hasWorkflowV2 && workflowV2StageTitle
      ? workflowV2StageTitle
      : legacyStepTitle) || "—";

  if (!hasWorkflowV2) {
    return {
      ...row,
      stepTitle,
    };
  }

  const blocking =
    row.workflowV2BlockingReason ??
    row.WorkflowV2BlockingReason ??
    row.workflowV2Blocking ??
    row.WorkflowV2Blocking ??
    "";

  return {
    ...row,
    stepTitle,
    scheduledVisitTime:
      row.scheduledVisitTime ?? row.ScheduledVisitTime ?? null,
    workflowV2StageTitle: workflowV2StageTitle || "—",
    workflowV2NextAction:
      row.workflowV2NextAction ??
      row.WorkflowV2NextActionTitle ??
      row.WorkflowV2NextAction ??
      "—",
    workflowV2NextRole:
      row.workflowV2NextRole ??
      row.WorkflowV2NextActionRole ??
      row.WorkflowV2NextRole ??
      "—",
    workflowV2Blocking: blocking,
    workflowV2ChipColor: blocking ? "error" : "primary",
  };
};

export const collectRequestFiles = (request = {}) => [
  { label: "محل نصب ۱", url: request.plantInstallImage1Url },
  { label: "محل نصب ۲", url: request.plantInstallImage2Url },
  { label: "قبض برق", url: request.electricBoillUrl },
  { label: "سند مالکیت", url: request.homeOwnerShipDocUrl },
  { label: "کارت ملی", url: request.nationalCardUrl },
  { label: "شناسنامه", url: request.nationalCardBackUrl },
  { label: "رضایت‌نامه هیئت‌مدیره", url: request.boardConsentDocumentUrl },
  { label: "نامه نماینده", url: request.authorizedRepresentativeLetterUrl },
  { label: "تفاهم‌نامه", url: request.agreementDocumentUrl },
].filter((f) => f.url != null && String(f.url).trim() !== "");

export const parseAllRequestsDetail = (res) => {
  const payload = res?.data?.data ?? res?.data ?? res ?? {};
  return {
    request: payload.request ?? payload.Request ?? null,
    followUps: payload.followUps ?? payload.FollowUps ?? [],
    history:
      payload.requestUpdateHistoryList ??
      payload.RequestUpdateHistoryList ??
      [],
    baseUrl: payload.baseUrl ?? payload.BaseUrl ?? "",
    stepList: payload.stepList ?? payload.StepList ?? [],
  };
};

const UPDATE_TYPE_LABELS = {
  Assigned: "ارجاع / تخصیص",
  Rejected: "رد",
  Approved: "تایید",
  Edited: "ویرایش",
  1: "ارجاع / تخصیص",
  2: "رد",
  3: "تایید",
  4: "ویرایش",
};

export const formatUpdateTypeLabel = (updateType) =>
  UPDATE_TYPE_LABELS[updateType] ??
  (updateType != null ? String(updateType) : "—");
